import {
  BuildingPackageType,
  DocumentPackageType,
  LayoutType,
  MeetingSecretaryType,
  QaAgentType,
  ServiceName,
  ServiceNameType,
  ServicePermissionName,
  SolutionViewerType,
} from '@consigli/types';

import CeilingPlanIcon from '@/assets/images/services/ceiling-plan-icon.png';
import CodeResearchAgentIcon from '@/assets/images/services/code-research-agent-icon.png';
import DatacenterIcon from '@/assets/images/services/datacenter.png';
import DesignPackageReviewIcon from '@/assets/images/services/design_package_review.png';
import DueDiligenceIcon from '@/assets/images/services/due-diligence-icon.png';
import DueDiligenceSitesIcon from '@/assets/images/services/due-diligence-site-icon.png';
import FloorPlanIcon from '@/assets/images/services/floor-plan-icon.png';
import GeotechnicalExtractionAgentIcon from '@/assets/images/services/geotechnical-extraction-agent-icon.png';
import LoadCalculationIcon from '@/assets/images/services/load-calculation-icon.png';
import LuminaireScheduleIcon from '@/assets/images/services/luminaire-schedule-icon.png';
import MeetingSecretaryIcon from '@/assets/images/services/meeting-secretary-icon.png';
import OfferIcon from '@/assets/images/services/offer-icon.png';
import OperationsManagementIcon from '@/assets/images/services/operations-management-icon.png';
import ParkingIcon from '@/assets/images/services/parking-icon.png';
import PlantRoomIcon from '@/assets/images/services/plant-room-icon.png';
import QaAgentIcon from '@/assets/images/services/qa-agent-icon.png';
import ReportsIcon from '@/assets/images/services/reports-icon.png';
import SpaceIcon from '@/assets/images/services/space-analysis-icon.png';
// Stand-in card image for Wastewater treatment until it has one of its own:
// the structural icon is the one image no other entry references.
import WastewaterTreatmentIcon from '@/assets/images/services/structural-icon.png';
import TenantRequirementIcon from '@/assets/images/services/tenant-requirement-icon.png';
import TenderDocumentsIcon from '@/assets/images/services/tender-documents-icon.png';
import VersionCompareIcon from '@/assets/images/services/version-compare-icon.png';
import { Route } from '@/routes';

const ContractReviewIcon = TenderDocumentsIcon;

// Stand-in card image for the Environment Report Agent until it has one of its own.
const EnvironmentReportAgentIcon = ReportsIcon;

// Stand-in card image for the Lightning Designer until it has one of its own:
// it designs from an IFC model, as Solution Viewer does.
const LightningDesignerIcon = CeilingPlanIcon;

// Reuse the ceiling plan icon for Solution Viewer
const SolutionViewerIcon = CeilingPlanIcon;

// Global switch for the "Beta" badge on service cards. Per-service `isBeta`
// metadata is kept below; flip this to re-enable the badges everywhere.
export const SHOW_BETA_BADGES = false;

/**
 * The agents directory's top-level grouping, from the label at the top right of
 * each agent's page in AI4Eng_Capability_Overviews.pdf. An agent carries one or
 * more. The order is the filter's, and follows the order each category first
 * appears in that document.
 *
 * Each value is both the filter pill's value and its i18n key suffix
 * (`agents-directory.categories.<value>`), so a rename here is a rename in
 * eleven locale files. `agents-directory-keys.test.ts` fails until they agree.
 */
export const AGENT_CATEGORY = {
  COMMERCIAL: 'commercial',
  ENGINEERING: 'engineering',
  CHECKER: 'checker',
  REPORTING: 'reporting',
  QUALITY: 'quality',
  COLLABORATION: 'collaboration',
  WORK_WINNING: 'work-winning',
  WATER: 'water',
} as const;

export type AgentCategory = (typeof AGENT_CATEGORY)[keyof typeof AGENT_CATEGORY];

/**
 * The descriptive pills on an agent's card, 1-4 each. Descriptive only: nothing
 * keys on them, unlike `categories`.
 *
 * Closed rather than `string[]` because a typo would otherwise ship as its own
 * tag, read as a real one, and render as the raw key. Every member needs a label
 * in all eleven locales under `agents-directory.tags.<tag>`, and the contract
 * test checks both directions, so an unused member is as loud as a missing one.
 *
 * Still engineering's first pass, unlike the categories: product owns which
 * agents are listed and under what, and is supplying the tag vocabulary later.
 */
export type AgentTag =
  | 'bids'
  | 'bim'
  | 'comparison'
  | 'compliance'
  | 'data'
  | 'documents'
  | 'geotechnical'
  | 'handover'
  | 'layout'
  | 'meetings'
  | 'mep'
  | 'quality'
  | 'requirements'
  | 'research'
  | 'risk'
  | 'schedules'
  | 'site'
  | 'tender'
  | 'visualization'
  | 'water';

/**
 * One entry in `serviceConfig`. The four required fields are what every consumer
 * reads; the rest describe how a service is reached, and three entries have no
 * route at all.
 *
 * `category` and `tags` are the exception: carrying them is what puts an agent
 * in the directory. Product named eighteen of the thirty-one, so an entry
 * without a category is deliberately absent from that page rather than
 * half-configured, and a new service stays off it until someone says otherwise.
 * They come as a pair — `service-config.test.ts` holds that.
 *
 * Applied with `satisfies`, not `:`. An annotation would widen every entry to
 * this type and `use-project-services.ts` would lose the optional fields it
 * reads off the inferred literals.
 */
export interface ServiceConfigEntry {
  serviceName: ServiceNameType;
  /** Raster card icon. The directory uses the inline SVG marks instead. */
  icon: string;
  tooltipKey: string;
  titleKey: string;
  /** Present on the eighteen agents the directory lists, absent on the rest. One or more. */
  categories?: readonly AgentCategory[];
  tags?: readonly AgentTag[];
  /** The directory card's one-line description, under `agents-directory.descriptions`. */
  directoryDescriptionKey?: string;
  servicePermissionName?: ServicePermissionName;
  serviceType?: ServiceName;
  serviceRoute?: string;
  /**
   * Fragment under `/projects/:id/services/`, e.g. `due-diligence/create`;
   * `useServiceNavigation` prefixes it. Solution Viewer's bare `Route.SOLUTION_VIEWER`
   * never reaches that prefix because `externalToSolutionViewer` intercepts first.
   */
  newServiceRoute?: string;
  serviceId?:
    | BuildingPackageType
    | DocumentPackageType
    | LayoutType
    | MeetingSecretaryType
    | QaAgentType
    | SolutionViewerType;
  alwaysActive?: boolean;
  isBeta?: boolean;
  externalUrl?: string;
  externalToSolutionViewer?: boolean;
  openInSameWindow?: boolean;
  /**
   * Translation keys naming the two extracts of a finding that compares two
   * documents. Services without it label both `findings.extract`.
   */
  comparisonExtractLabelKeys?: { primary: string; reference: string };
}

const BID_AGAINST_TENDER_LABEL_KEYS = {
  primary: 'findings.bid-extract',
  reference: 'findings.tender-extract',
};

export const serviceConfig = [
  {
    serviceName: ServiceNameType.LUMINAIRE_SCHEDULE,
    servicePermissionName: ServicePermissionName.USE_LUMINAIRE_SCHEDULE_SERVICE,
    serviceType: ServiceName.LUMINAIRE_SCHEDULE,
    icon: LuminaireScheduleIcon,
    tooltipKey: 'service-tooltips.luminaire-schedule',
    titleKey: 'services.luminaire-schedule',
    directoryDescriptionKey: 'agents-directory.descriptions.luminaire-schedule',
    categories: [AGENT_CATEGORY.ENGINEERING],
    tags: ['mep', 'schedules', 'documents'],
    newServiceRoute: `${Route.LUMINAIRE_SCHEDULE}/${Route.CREATE}`,
    serviceRoute: Route.LUMINAIRE_SCHEDULE,
    serviceId: DocumentPackageType.LUMINAIRE_SCHEDULE,
  },
  {
    serviceName: ServiceNameType.DUE_DILIGENCE,
    servicePermissionName: ServicePermissionName.USE_DUE_DILIGENCE_SERVICE,
    serviceType: ServiceName.DUE_DILIGENCE,
    icon: DueDiligenceIcon,
    tooltipKey: 'service-tooltips.due-diligence',
    titleKey: 'services.due-diligence',
    newServiceRoute: `${Route.DUE_DILIGENCE}/${Route.CREATE}`,
    serviceRoute: Route.DUE_DILIGENCE,
    serviceId: DocumentPackageType.DUE_DILIGENCE,
  },
  {
    serviceName: ServiceNameType.SITE_DUE_DILIGENCE,
    servicePermissionName: ServicePermissionName.USE_SITE_DUE_DILIGENCE_SERVICE,
    serviceType: ServiceName.SITE_DUE_DILIGENCE,
    icon: DueDiligenceSitesIcon,
    tooltipKey: 'service-tooltips.site-due-diligence',
    titleKey: 'services.site-due-diligence',
    directoryDescriptionKey: 'agents-directory.descriptions.site-due-diligence',
    categories: [AGENT_CATEGORY.REPORTING],
    tags: ['risk', 'site', 'documents'],
    newServiceRoute: `${Route.SITE_DUE_DILIGENCE}/${Route.CREATE}`,
    serviceRoute: Route.SITE_DUE_DILIGENCE,
    serviceId: DocumentPackageType.SITE_DUE_DILIGENCE,
  },
  {
    serviceName: ServiceNameType.TENDER_DOCUMENTS,
    servicePermissionName: ServicePermissionName.USE_TENDER_DOCUMENTS_SERVICE,
    serviceType: ServiceName.TENDER_DOCUMENTS,
    icon: TenderDocumentsIcon,
    tooltipKey: 'service-tooltips.tender-documents',
    titleKey: 'services.tender-documents',
    directoryDescriptionKey: 'agents-directory.descriptions.tender-documents',
    categories: [AGENT_CATEGORY.QUALITY],
    tags: ['tender', 'risk', 'documents'],
    newServiceRoute: `${Route.TENDER_DOCUMENTS}/${Route.CREATE}`,
    serviceRoute: Route.TENDER_DOCUMENTS,
    serviceId: DocumentPackageType.TENDER_DOCUMENTS,
  },
  {
    serviceName: ServiceNameType.RFP_RFQ_REVIEW,
    servicePermissionName: ServicePermissionName.USE_RFP_RFQ_REVIEW_SERVICE,
    serviceType: ServiceName.RFP_RFQ_REVIEW,
    icon: TenderDocumentsIcon,
    tooltipKey: 'service-tooltips.rfp-rfq-review',
    titleKey: 'services.rfp-rfq-review',
    directoryDescriptionKey: 'agents-directory.descriptions.rfp-rfq-review',
    categories: [AGENT_CATEGORY.WORK_WINNING],
    tags: ['tender', 'compliance', 'risk'],
    newServiceRoute: `${Route.RFP_RFQ_REVIEW}/${Route.CREATE}`,
    serviceRoute: Route.RFP_RFQ_REVIEW,
    serviceId: DocumentPackageType.RFP_RFQ_REVIEW,
    isBeta: true,
  },
  {
    serviceName: ServiceNameType.DESIGN_PACKAGE_REVIEW,
    servicePermissionName: ServicePermissionName.USE_DESIGN_PACKAGE_REVIEW_SERVICE,
    serviceType: ServiceName.DESIGN_PACKAGE_REVIEW,
    icon: DesignPackageReviewIcon,
    tooltipKey: 'service-tooltips.design-package-review',
    titleKey: 'services.design-package-review',
    directoryDescriptionKey: 'agents-directory.descriptions.design-package-review',
    categories: [AGENT_CATEGORY.QUALITY],
    tags: ['quality', 'compliance', 'documents'],
    newServiceRoute: `${Route.DESIGN_PACKAGE_REVIEW}/${Route.CREATE}`,
    serviceRoute: Route.DESIGN_PACKAGE_REVIEW,
    serviceId: DocumentPackageType.DESIGN_PACKAGE_REVIEW,
    isBeta: true,
  },
  {
    serviceName: ServiceNameType.OFFER,
    servicePermissionName: ServicePermissionName.USE_OFFER_SERVICE,
    serviceType: ServiceName.OFFER,
    icon: OfferIcon,
    tooltipKey: 'service-tooltips.offer',
    titleKey: 'services.offer',
    directoryDescriptionKey: 'agents-directory.descriptions.offer',
    categories: [AGENT_CATEGORY.COMMERCIAL],
    tags: ['bids', 'risk', 'documents'],
    newServiceRoute: `${Route.OFFER}/${Route.CREATE}`,
    serviceRoute: Route.OFFER,
    serviceId: DocumentPackageType.OFFER,
    comparisonExtractLabelKeys: BID_AGAINST_TENDER_LABEL_KEYS,
  },
  {
    serviceName: ServiceNameType.BID_REVIEW,
    servicePermissionName: ServicePermissionName.USE_OFFER_SERVICE,
    serviceType: ServiceName.OFFER,
    icon: OfferIcon,
    tooltipKey: 'service-tooltips.bid-review',
    titleKey: 'services.bid-review',
    newServiceRoute: `${Route.BID_REVIEW}/${Route.CREATE}`,
    serviceRoute: Route.BID_REVIEW,
    serviceId: DocumentPackageType.OFFER,
    isBeta: true,
    comparisonExtractLabelKeys: BID_AGAINST_TENDER_LABEL_KEYS,
  },
  {
    serviceName: ServiceNameType.TENANT_REQUIREMENTS,
    servicePermissionName: ServicePermissionName.USE_TENANT_REQ_SERVICE,
    serviceType: ServiceName.TENANT_REQUIREMENTS,
    icon: TenantRequirementIcon,
    tooltipKey: 'service-tooltips.tenant-requirements',
    titleKey: 'services.tenant-requirements',
    newServiceRoute: `${Route.TENANT_REQUIREMENTS}/${Route.CREATE}`,
    serviceRoute: Route.TENANT_REQUIREMENTS,
    serviceId: DocumentPackageType.TENANT_REQUIREMENTS,
  },
  {
    serviceName: ServiceNameType.MANAGEMENT_OPERATIONS_MAINTENANCE,
    servicePermissionName: ServicePermissionName.USE_MANAGE_OPERATE_SERVICE,
    serviceType: ServiceName.MANAGEMENT_OPERATIONS_MAINTENANCE,
    icon: OperationsManagementIcon,
    tooltipKey: 'service-tooltips.management-operations-maintenance',
    titleKey: 'services.management-operations-maintenance',
    directoryDescriptionKey: 'agents-directory.descriptions.management-operations-maintenance',
    categories: [AGENT_CATEGORY.QUALITY],
    tags: ['handover', 'risk', 'documents'],
    newServiceRoute: `${Route.MANAGEMENT_OPERATIONS_MAINTENANCE}/${Route.CREATE}`,
    serviceRoute: Route.MANAGEMENT_OPERATIONS_MAINTENANCE,
    serviceId: DocumentPackageType.MANAGEMENT_OPERATIONS_MAINTENANCE,
  },
  {
    serviceName: ServiceNameType.CEILING_PLAN,
    servicePermissionName: ServicePermissionName.USE_CEILING_PLAN_SERVICE,
    serviceType: ServiceName.CEILING_PLAN,
    icon: CeilingPlanIcon,
    tooltipKey: 'service-tooltips.ceiling-plan',
    titleKey: 'services.ceiling-plan',
    newServiceRoute: `${Route.CEILING_PLAN}/${Route.CREATE}`,
    serviceRoute: Route.CEILING_PLAN,
    serviceId: LayoutType.CEILING_PLAN,
  },
  {
    serviceName: ServiceNameType.LOAD_CALCULATION,
    servicePermissionName: ServicePermissionName.USE_LOAD_CALCULATION_SERVICE,
    serviceType: ServiceName.LOAD_CALCULATION,
    icon: LoadCalculationIcon,
    tooltipKey: 'service-tooltips.load-calculation',
    titleKey: 'services.load-calculation',
    newServiceRoute: `${Route.LOAD_CALCULATION}/${Route.CREATE}`,
    serviceRoute: Route.LOAD_CALCULATION,
    serviceId: BuildingPackageType.LOAD_CALCULATION,
  },
  {
    serviceName: ServiceNameType.PLANT_ROOM,
    servicePermissionName: ServicePermissionName.USE_PLANT_ROOM_SERVICE,
    serviceType: ServiceName.PLANT_ROOM,
    icon: PlantRoomIcon,
    tooltipKey: 'service-tooltips.plant-room',
    titleKey: 'services.plant-room',
    newServiceRoute: `${Route.PLANT_ROOM}/${Route.CREATE}`,
    serviceRoute: Route.PLANT_ROOM,
    serviceId: LayoutType.PLANT_ROOM,
  },
  {
    serviceName: ServiceNameType.FLOOR_PLAN,
    servicePermissionName: ServicePermissionName.USE_FLOOR_PLAN_SERVICE,
    serviceType: ServiceName.FLOOR_PLAN,
    icon: FloorPlanIcon,
    tooltipKey: 'service-tooltips.floor-plan',
    titleKey: 'services.floor-plan',
    newServiceRoute: `${Route.FLOOR_PLAN}/${Route.CREATE}`,
    serviceRoute: Route.FLOOR_PLAN,
    serviceId: LayoutType.FLOOR_PLAN,
  },
  {
    serviceName: ServiceNameType.SPACE_ANALYSIS,
    servicePermissionName: ServicePermissionName.USE_SPACE_ANALYSIS_SERVICE,
    serviceType: ServiceName.SPACE_ANALYSIS,
    icon: SpaceIcon,
    tooltipKey: 'service-tooltips.space-analysis',
    titleKey: 'services.space-analysis',
    directoryDescriptionKey: 'agents-directory.descriptions.space-analysis',
    categories: [AGENT_CATEGORY.QUALITY],
    tags: ['layout', 'requirements', 'bim'],
    newServiceRoute: `${Route.SPACE_ANALYSIS}/${Route.CREATE}`,
    serviceRoute: Route.SPACE_ANALYSIS,
    serviceId: LayoutType.SPACE_ANALYSIS,
  },
  {
    serviceName: ServiceNameType.PARKING,
    servicePermissionName: ServicePermissionName.USE_PARKING_SERVICE,
    serviceType: ServiceName.PARKING,
    icon: ParkingIcon,
    tooltipKey: 'service-tooltips.parking',
    titleKey: 'services.parking',
    newServiceRoute: `${Route.PARKING}/${Route.CREATE}`,
    serviceRoute: Route.PARKING,
    serviceId: LayoutType.PARKING,
  },
  {
    serviceName: ServiceNameType.REPORTS,
    servicePermissionName: ServicePermissionName.USE_REPORTS_SERVICE,
    serviceType: ServiceName.REPORTS,
    icon: ReportsIcon,
    tooltipKey: 'service-tooltips.reports',
    titleKey: 'services.reports',
    newServiceRoute: `${Route.REPORTS}/${Route.CREATE}`,
    serviceRoute: Route.REPORTS,
    serviceId: LayoutType.REPORTS,
  },
  {
    serviceName: ServiceNameType.REPORT_GENERATION,
    servicePermissionName: ServicePermissionName.USE_REPORT_GENERATION_SERVICE,
    serviceType: ServiceName.REPORT_GENERATION,
    icon: ReportsIcon,
    tooltipKey: 'service-tooltips.report-generation',
    titleKey: 'services.report-generation',
    newServiceRoute: `${Route.REPORT_GENERATION}/${Route.CREATE}`,
    serviceRoute: Route.REPORT_GENERATION,
    serviceId: DocumentPackageType.REPORT_GENERATION,
  },
  {
    serviceName: ServiceNameType.SCHEDULE_CHECK,
    servicePermissionName: ServicePermissionName.USE_SCHEDULE_CHECK_SERVICE,
    serviceType: ServiceName.SCHEDULE_CHECK,
    icon: ReportsIcon,
    tooltipKey: 'service-tooltips.schedule-check',
    titleKey: 'services.schedule-check',
    directoryDescriptionKey: 'agents-directory.descriptions.schedule-check',
    categories: [AGENT_CATEGORY.CHECKER],
    tags: ['schedules', 'compliance', 'comparison'],
    newServiceRoute: `${Route.SCHEDULE_CHECK}/${Route.CREATE}`,
    serviceRoute: Route.SCHEDULE_CHECK,
    serviceId: DocumentPackageType.SCHEDULE_CHECK,
  },
  {
    serviceName: ServiceNameType.CONTRACT_REVIEW,
    servicePermissionName: ServicePermissionName.USE_CONTRACT_REVIEW_SERVICE,
    serviceType: ServiceName.CONTRACT_REVIEW,
    icon: ContractReviewIcon,
    tooltipKey: 'service-tooltips.contract-review',
    titleKey: 'services.contract-review',
    newServiceRoute: `${Route.CONTRACT_REVIEW}/${Route.CREATE}`,
    serviceRoute: Route.CONTRACT_REVIEW,
    serviceId: DocumentPackageType.CONTRACT_REVIEW,
  },
  {
    serviceName: ServiceNameType.SOLUTION_VIEWER,
    servicePermissionName: ServicePermissionName.USE_SOLUTION_VIEWER_SERVICE,
    serviceType: ServiceName.SOLUTION_VIEWER,
    icon: SolutionViewerIcon,
    tooltipKey: 'service-tooltips.solution-viewer',
    titleKey: 'services.solution-viewer',
    newServiceRoute: Route.SOLUTION_VIEWER,
    serviceRoute: Route.SOLUTION_VIEWER,
    serviceId: SolutionViewerType.SOLUTION_VIEWER,
    alwaysActive: true,
    externalToSolutionViewer: true,
  },
  {
    serviceName: ServiceNameType.DATA_CENTER,
    icon: DatacenterIcon,
    tooltipKey: 'service-tooltips.data-center',
    titleKey: 'services.data-center',
    directoryDescriptionKey: 'agents-directory.descriptions.data-center',
    categories: [AGENT_CATEGORY.ENGINEERING],
    tags: ['data', 'visualization'],
    alwaysActive: true,
    externalToSolutionViewer: true,
  },
  {
    // Opens the Solution Viewer's /water_treatment page (see use-service-navigation.ts).
    // Like Data Center, no project permission: a per-user toggle decides access.
    serviceName: ServiceNameType.WASTEWATER_TREATMENT,
    icon: WastewaterTreatmentIcon,
    tooltipKey: 'service-tooltips.wastewater-treatment',
    titleKey: 'services.wastewater-treatment',
    categories: [AGENT_CATEGORY.ENGINEERING],
    tags: ['water', 'layout', 'visualization'],
    alwaysActive: true,
    externalToSolutionViewer: true,
  },
  {
    serviceName: ServiceNameType.MEETING_SECRETARY,
    servicePermissionName: ServicePermissionName.USE_MEETING_SECRETARY_SERVICE,
    serviceType: ServiceName.MEETING_SECRETARY,
    icon: MeetingSecretaryIcon,
    tooltipKey: 'service-tooltips.meeting-secretary',
    titleKey: 'services.meeting-secretary',
    directoryDescriptionKey: 'agents-directory.descriptions.meeting-secretary',
    categories: [AGENT_CATEGORY.COLLABORATION],
    tags: ['meetings', 'documents'],
    newServiceRoute: `${Route.MEETING_SECRETARY}/${Route.DASHBOARD}`,
    serviceRoute: Route.MEETING_SECRETARY,
    serviceId: MeetingSecretaryType.MEETING_SECRETARY,
    alwaysActive: true,
    isBeta: true,
  },
  {
    serviceName: ServiceNameType.QA_AGENT,
    servicePermissionName: ServicePermissionName.USE_QA_AGENT_SERVICE,
    serviceType: ServiceName.QA_AGENT,
    icon: QaAgentIcon,
    tooltipKey: 'service-tooltips.qa-agent',
    titleKey: 'services.qa-agent',
    directoryDescriptionKey: 'agents-directory.descriptions.qa-agent',
    categories: [AGENT_CATEGORY.QUALITY],
    tags: ['quality', 'compliance'],
    newServiceRoute: `${Route.QA_AGENT}/${Route.CREATE}`,
    serviceRoute: Route.QA_AGENT,
    serviceId: QaAgentType.QA_AGENT,
  },
  {
    serviceName: ServiceNameType.CODE_RESEARCH_AGENT,
    servicePermissionName: ServicePermissionName.USE_CODE_RESEARCH_AGENT_SERVICE,
    serviceType: ServiceName.CODE_RESEARCH_AGENT,
    icon: CodeResearchAgentIcon,
    tooltipKey: 'service-tooltips.code-research-agent',
    titleKey: 'services.code-research-agent',
    directoryDescriptionKey: 'agents-directory.descriptions.code-research-agent',
    categories: [AGENT_CATEGORY.ENGINEERING],
    tags: ['research', 'compliance'],
    externalUrl: import.meta.env.VITE_CODE_RESEARCH_AGENT_URL,
    openInSameWindow: true,
    isBeta: true,
  },
  {
    serviceName: ServiceNameType.VERSION_COMPARE,
    servicePermissionName: ServicePermissionName.USE_VERSION_COMPARE_SERVICE,
    serviceType: ServiceName.VERSION_COMPARE,
    icon: VersionCompareIcon,
    tooltipKey: 'service-tooltips.version-compare',
    titleKey: 'services.version-compare',
    directoryDescriptionKey: 'agents-directory.descriptions.version-compare',
    categories: [AGENT_CATEGORY.QUALITY],
    tags: ['comparison', 'documents'],
    newServiceRoute: `${Route.VERSION_COMPARE}/${Route.CREATE}`,
    serviceRoute: Route.VERSION_COMPARE,
    serviceId: DocumentPackageType.VERSION_COMPARE,
    isBeta: true,
  },
  {
    serviceName: ServiceNameType.WATER_PROCESS_SYSTEMS_AGENT,
    servicePermissionName: ServicePermissionName.USE_WATER_PROCESS_SYSTEMS_AGENT_SERVICE,
    serviceType: ServiceName.WATER_PROCESS_SYSTEMS_AGENT,
    icon: OperationsManagementIcon,
    tooltipKey: 'service-tooltips.water-process-systems-agent',
    titleKey: 'services.water-process-systems-agent',
    directoryDescriptionKey: 'agents-directory.descriptions.water-process-systems-agent',
    categories: [AGENT_CATEGORY.QUALITY, AGENT_CATEGORY.WATER],
    tags: ['water', 'compliance', 'risk'],
    newServiceRoute: `${Route.WATER_PROCESS_SYSTEMS_AGENT}/${Route.CREATE}`,
    serviceRoute: Route.WATER_PROCESS_SYSTEMS_AGENT,
    serviceId: DocumentPackageType.WATER_PROCESS_SYSTEMS_AGENT,
    isBeta: true,
  },
  {
    serviceName: ServiceNameType.GEOTECHNICAL_EXTRACTION_AGENT,
    servicePermissionName: ServicePermissionName.USE_GEOTECHNICAL_EXTRACTION_AGENT_SERVICE,
    icon: GeotechnicalExtractionAgentIcon,
    tooltipKey: 'service-tooltips.geotechnical-extraction-agent',
    titleKey: 'services.geotechnical-extraction-agent',
    directoryDescriptionKey: 'agents-directory.descriptions.geotechnical-extraction-agent',
    categories: [AGENT_CATEGORY.ENGINEERING],
    tags: ['geotechnical', 'data', 'documents'],
    // Standalone agent app; URL differs per environment (see apps/frontend/.env.*).
    externalUrl: import.meta.env.VITE_GEOTECHNICAL_EXTRACTION_AGENT_URL,
    openInSameWindow: true,
    isBeta: true,
  },
  {
    serviceName: ServiceNameType.ENVIRONMENT_REPORT_AGENT,
    servicePermissionName: ServicePermissionName.USE_ENVIRONMENT_REPORT_AGENT_SERVICE,
    // Applied once ERW records a package through core-api's used-external-services
    // endpoint, which puts this name in the project's activeServices.
    serviceType: ServiceName.ENVIRONMENT_REPORT_AGENT,
    icon: EnvironmentReportAgentIcon,
    tooltipKey: 'service-tooltips.environment-report-agent',
    titleKey: 'services.environment-report-agent',
    // Standalone agent app, reached only through this URL. `useProjectServices`
    // shows the card to superusers only for now, and drops it where the URL is
    // unset rather than render one that goes nowhere.
    // No category, so it stays off the agents directory until product lists it.
    externalUrl: import.meta.env.VITE_ENVIRONMENT_REPORT_AGENT_URL,
    openInSameWindow: true,
    isBeta: true,
  },
  {
    serviceName: ServiceNameType.LIGHTNING_DESIGNER,
    servicePermissionName: ServicePermissionName.USE_LIGHTNING_DESIGNER_SERVICE,
    // Applied once the Lightning Designer records a use through core-api's
    // used-external-services endpoint, which puts this name in the project's
    // activeServices.
    serviceType: ServiceName.LIGHTNING_DESIGNER,
    icon: LightningDesignerIcon,
    tooltipKey: 'service-tooltips.lightning-designer',
    titleKey: 'services.lightning-designer',
    directoryDescriptionKey: 'agents-directory.descriptions.lightning-designer',
    categories: [AGENT_CATEGORY.ENGINEERING],
    tags: ['bim', 'risk', 'compliance', 'visualization'],
    // Standalone agent app, reached only through this URL. `useProjectServices`
    // drops the card where the URL is unset rather than render one that goes
    // nowhere.
    externalUrl: import.meta.env.VITE_LIGHTNING_DESIGNER_URL,
    openInSameWindow: true,
    isBeta: true,
  },
  // Add more services as needed
] satisfies readonly ServiceConfigEntry[];

/** The entry whose `serviceRoute` is the `:serviceName` segment of the current URL. */
export const findServiceConfigByRoute = (route: string | null): ServiceConfigEntry | undefined =>
  serviceConfig.find((entry) => entry.serviceRoute === route);

export const superUserDeveloperServicesOrder = [
  'DUE_DILIGENCE',
  'SITE_DUE_DILIGENCE',
  'SPACE_ANALYSIS',
  'FLOOR_PLAN',
  'PARKING',
  'LOAD_CALCULATION',
  'CEILING_PLAN',
  'REPORTS',
  'REPORT_GENERATION',
  'SCHEDULE_CHECK',
  'LUMINAIRE_SCHEDULE',
  'CONTRACT_REVIEW',
  'SOLUTION_VIEWER',
  'DATA_CENTER',
  'WASTEWATER_TREATMENT',
  'MEETING_SECRETARY',
  'PLANT_ROOM',
  'TENDER_DOCUMENTS',
  'RFP_RFQ_REVIEW',
  'DESIGN_PACKAGE_REVIEW',
  'OFFER',
  'BID_REVIEW',
  'TENANT_REQUIREMENTS',
  'MANAGEMENT_OPERATIONS_MAINTENANCE',
  'WATER_PROCESS_SYSTEMS_AGENT',
  // Dead entry: no serviceConfig service carries this name.
  'CEILING_GRID',
  'QA_AGENT',
  'VERSION_COMPARE',
  'GEOTECHNICAL_EXTRACTION_AGENT',
  'ENVIRONMENT_REPORT_AGENT',
  'LIGHTNING_DESIGNER',
];

export const developerServicesOrder = [
  'DUE_DILIGENCE',
  'SITE_DUE_DILIGENCE',
  'SPACE_ANALYSIS',
  'FLOOR_PLAN',
  'PARKING',
  'LOAD_CALCULATION',
  'CEILING_PLAN',
  'REPORTS',
  'SCHEDULE_CHECK',
  'LUMINAIRE_SCHEDULE',
  'CONTRACT_REVIEW',
  'MEETING_SECRETARY',
  'PLANT_ROOM',
  'TENDER_DOCUMENTS',
  'RFP_RFQ_REVIEW',
  'DESIGN_PACKAGE_REVIEW',
  'OFFER',
  'TENANT_REQUIREMENTS',
  'MANAGEMENT_OPERATIONS_MAINTENANCE',
  'QA_AGENT',
  'GEOTECHNICAL_EXTRACTION_AGENT',
  'ENVIRONMENT_REPORT_AGENT',
  'LIGHTNING_DESIGNER',
];

// Unused: service-list-page.tsx passes superUserDeveloperServicesOrder for the
// superuser-on-contractor case. Kept pending a product decision.
export const superUserContractorServicesOrder = [
  'TENDER_DOCUMENTS',
  'RFP_RFQ_REVIEW',
  'DESIGN_PACKAGE_REVIEW',
  'OFFER',
  'BID_REVIEW',
  'CONTRACT_REVIEW',
  'SPACE_ANALYSIS',
  'FLOOR_PLAN',
  'PARKING',
  'LOAD_CALCULATION',
  'CEILING_PLAN',
  'REPORTS',
  'REPORT_GENERATION',
  'SCHEDULE_CHECK',
  'LUMINAIRE_SCHEDULE',
  'SOLUTION_VIEWER',
  'DATA_CENTER',
  'WASTEWATER_TREATMENT',
  'MEETING_SECRETARY',
  'PLANT_ROOM',
  'TENANT_REQUIREMENTS',
  'MANAGEMENT_OPERATIONS_MAINTENANCE',
  'WATER_PROCESS_SYSTEMS_AGENT',
  // Dead entry: no serviceConfig service carries this name.
  'CEILING_GRID',
  'QA_AGENT',
  'VERSION_COMPARE',
  'GEOTECHNICAL_EXTRACTION_AGENT',
  'ENVIRONMENT_REPORT_AGENT',
  'LIGHTNING_DESIGNER',
];

export const contractorServicesOrder = [
  'TENDER_DOCUMENTS',
  'RFP_RFQ_REVIEW',
  'DESIGN_PACKAGE_REVIEW',
  'OFFER',
  'CONTRACT_REVIEW',
  'SPACE_ANALYSIS',
  'FLOOR_PLAN',
  'PARKING',
  'LOAD_CALCULATION',
  'CEILING_PLAN',
  'REPORTS',
  'SCHEDULE_CHECK',
  'LUMINAIRE_SCHEDULE',
  'MEETING_SECRETARY',
  'PLANT_ROOM',
  'TENANT_REQUIREMENTS',
  'MANAGEMENT_OPERATIONS_MAINTENANCE',
  'QA_AGENT',
  'GEOTECHNICAL_EXTRACTION_AGENT',
  'ENVIRONMENT_REPORT_AGENT',
  'LIGHTNING_DESIGNER',
];
